import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Home, Calendar, Clock, MapPin, User, Mail, Phone, Loader2, MessageCircle } from 'lucide-react';
import { getBookingById } from '../services/firestore';
import { formatCurrency, formatDateShort } from '../types/booking';
import SEO from '../components/SEO';

const BookingConfirmation = () => {
  const { ref } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!ref) {
        setError('Booking reference tidak ditemukan');
        setLoading(false);
        return;
      }

      try {
        const data = await getBookingById(ref);
        setBooking(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Gagal memuat detail booking. Booking mungkin tidak ditemukan.');
        setLoading(false);
      }
    };

    fetchBooking();
  }, [ref]);

  const formatWhatsAppMessage = () => {
    if (!booking) return '';

    const firstPackage = booking.items?.[0]?.packageSnapshot?.name || 'henna';
    const message = `Halo teh, aku mau booking ${firstPackage} untuk tanggal ${formatDateShort(booking.eventDate)}

Booking: ${booking.bookingNumber}
Nama: ${booking.clientName}
Jam: ${booking.eventTime || 'TBD'}
Lokasi: ${booking.eventLocation}
Total: ${formatCurrency(booking.totalAmount)}
Deposit: ${formatCurrency(booking.depositAmount)}`;

    return encodeURIComponent(message);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-[#D4AF7A] mx-auto mb-4" />
          <p className="text-[#F7F5F2]">Memuat detail booking...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-[#F7F5F2] mb-4">
              Booking Tidak Ditemukan
            </h1>
            <p className="text-[#F7F5F2]/70 mb-6">
              {error || 'Maaf, detail booking tidak dapat ditemukan.'}
            </p>
            <button
              onClick={() => navigate('/booking')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Buat Booking Baru
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-[#0A0A0A] py-12 px-4">
      <SEO
        title="Booking Berhasil | Putri Henna Artist"
        description="Booking layanan henna Anda telah berhasil dikonfirmasi. Tim Putri Henna Artist akan menghubungi Anda untuk detail lebih lanjut."
        keywords="booking henna sukses, konfirmasi booking, henna wedding booking"
        url={`https://tehputrihenna.com/booking/confirmation`}
      />
      <div className="max-w-3xl mx-auto">
        {/* Success Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#C9A882]/20 to-[#D4AF7A]/20 p-8 text-center">
            <div className="w-20 h-20 bg-[#D4AF7A] rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={40} className="text-black" />
            </div>
            <h1 className="text-3xl font-bold text-[#F7F5F2] mb-2">
              Booking Berhasil! 🎉
            </h1>
            <p className="text-[#F7F5F2]/80">
              Terima kasih {booking.clientName?.split(' ')[0]}, booking Anda telah diterima
            </p>
          </div>

          {/* Booking Details */}
          <div className="p-6 sm:p-8 space-y-6">
            {/* Reference Number */}
            <div className="p-4 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-center">
              <p className="text-sm text-[#F7F5F2]/70 mb-1">Booking Number</p>
              <p className="text-2xl font-bold text-[#D4AF7A]">{booking.bookingNumber}</p>
            </div>

            {/* Estimated Time */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-400 mb-1">Estimasi Waktu</h3>
                  <p className="text-sm text-[#F7F5F2]/80">
                    Kami akan menghubungi Anda dalam <strong className="text-[#F7F5F2]">1-24 jam</strong> melalui WhatsApp untuk konfirmasi ketersediaan dan instruksi pembayaran deposit.
                  </p>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div>
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-3">Detail Acara</h3>
              <div className="space-y-3 p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-5 h-5 text-[#D4AF7A]" />
                  <span className="text-[#F7F5F2]/70">Tanggal:</span>
                  <span className="text-[#F7F5F2] font-medium">{formatDateShort(booking.eventDate)}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-5 h-5 text-[#D4AF7A]" />
                  <span className="text-[#F7F5F2]/70">Jam:</span>
                  <span className="text-[#F7F5F2] font-medium">{booking.eventTime || 'TBD'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-5 h-5 text-[#D4AF7A]" />
                  <span className="text-[#F7F5F2]/70">Lokasi:</span>
                  <span className="text-[#F7F5F2] font-medium">{booking.eventLocation}</span>
                </div>
              </div>
            </div>

            {/* Packages Summary */}
            {booking.items && booking.items.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-[#F7F5F2] mb-3">Paket yang Dipilih</h3>
                <div className="space-y-2">
                  {booking.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-[#F7F5F2]">
                          {item.packageSnapshot?.name || item.addOnSnapshot?.name}
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-xs text-[#F7F5F2]/60">x {item.quantity}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-[#D4AF7A]">
                        {formatCurrency(item.subtotal)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Summary */}
            <div>
              <h3 className="text-lg font-semibold text-[#F7F5F2] mb-3">Ringkasan Pembayaran</h3>
              <div className="p-4 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#F7F5F2]/80">Total Booking</span>
                  <span className="text-[#F7F5F2]">{formatCurrency(booking.totalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#F7F5F2]/80">Deposit ({booking.depositPercent || 30}%)</span>
                  <span className="text-[#D4AF7A] font-semibold">{formatCurrency(booking.depositAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#F7F5F2]/80">Pelunasan di Lokasi</span>
                  <span className="text-[#F7F5F2]">{formatCurrency(booking.remainingAmount)}</span>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <h4 className="font-semibold text-yellow-400 mb-2">Penting:</h4>
              <ul className="space-y-1 text-sm text-[#F7F5F2]/80">
                <li>• Transfer deposit dalam 24 jam setelah dikonfirmasi</li>
                <li>• Booking akan dibatalkan otomatis jika deposit tidak dibayar</li>
                <li>• Bawa bukti transfer pada hari acara</li>
                <li>• Hubungi kami jika ada perubahan jadwal</li>
              </ul>
            </div>

            {/* WhatsApp CTA */}
            <div className="space-y-3">
              <a
                href={`https://wa.me/6289526775445?text=${formatWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52.075-.149.669-1.612.916-2.207.242-.579.487-.5.669-.5.173-.008.371-.008.57-.008.198 0 .52.074.792.372.272.297 1.04 1.016 1.04 2.479 0 1.462-.997 2.848-1.138 3.001-.142.149-.474.462-.94.462-.198 0-.52-.074-.792-.198-.272-.149-.645-.371-.645-.371l-2.521-6.806c-.149-.397-.05-.792.149-.99.198-.198.521-.397.792-.397.272 0 .52.074.792.198.272.149.52.371.52.371 1.387 1.758 1.933 2.898 1.933 2.898.198.397.05.792-.149.99-.198.198-.521.397-.792.397-.272 0-.52-.074-.792-.198-.272-.149-.52-.371-.52-.371-1.584-1.958-2.203-3.597-2.203-3.597-.198-.496-.05-.99.149-1.238.198-.248.521-.496.792-.496.272 0 .52.074.792.198.272.149.52.371.52.371.997 1.213 1.545 2.079 1.545 2.079.198.297.05.595-.149.792-.198.198-.521.397-.792.397-.272 0-.52-.074-.792-.198-.272-.149-.52-.371-.52-.371-1.141-1.462-1.653-2.698-1.653-2.698-.149-.397.05-.792.248-.99.198-.198.521-.397.792-.397.272 0 .52.074.792.198.272.149.52.371.52.371.743.997 1.238 1.761 1.238 1.761.198.297.05.595-.149.792-.198.198-.397.05-.595-.149-.792z"/>
                </svg>
                Hubungi via WhatsApp
              </a>
              <p className="text-xs text-[#F7F5F2]/60 text-center">
                Klik tombol di atas untuk menghubungi kami dan konfirmasi booking
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-[#D4AF7A] hover:text-[#C9A882] transition-colors"
          >
            <Home size={18} />
            Kembali ke Beranda
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
